import axios from 'axios';
import { FlightDataTransformer } from '../transformers/FlightDataTransformer';

/**
 * FlightApiProvider - Wrapper for FlightApi.io service
 * Provides normalized flight search using the existing FlightApi.io endpoint
 */
export class FlightApiProvider {
    constructor() {
        this.transformer = new FlightDataTransformer();
        this.baseUrl = 'https://api.flightapi.io';
        this.apiKey = process.env.REACT_APP_FLIGHTAPI_KEY || '63c72d5bfd4bac12b478d7cc';
    }

    /**
     * Search for flights using FlightApi.io
     * @param {Object} params - Search parameters
     * @param {string} params.origin - Origin airport code
     * @param {string} params.destination - Destination airport code
     * @param {string} params.departureDate - Departure date (YYYY-MM-DD)
     * @param {string} params.returnDate - Return date (YYYY-MM-DD)
     * @param {number} params.adults - Number of adult passengers
     * @param {string} params.currency - Currency code (e.g., "CAD$", "USD$")
     * @returns {Promise<Object>} Normalized flight search results
     */
    async search(params) {
        const { origin, destination, departureDate, returnDate, adults, currency } = params;

        // Validate required parameters
        this.validateSearchParams(params);

        // Ensure adults doesn't exceed API limit
        const numPeople = Math.min(adults, 8);

        // Extract currency code (first 3 characters)
        const currencyCode = currency.substring(0, 3);

        // Build FlightApi.io URL
        const url = `${this.baseUrl}/roundtrip/${this.apiKey}/${origin}/${destination}/${departureDate}/${returnDate}/${numPeople}/0/0/Economy/${currencyCode}`;

        console.log(`FlightApiProvider: Searching flights with URL: ${url}`);

        try {
            const response = await axios.get(url, {
                timeout: 30000, // 30 second timeout
                headers: {
                    'Accept': 'application/json',
                    'User-Agent': 'Moniker-FlightTool/1.0'
                }
            });

            console.log('FlightApiProvider: Received response', {
                status: response.status,
                dataKeys: Object.keys(response.data || {}),
                itineraryCount: response.data?.itineraries?.length || response.data?.data?.itineraries?.length || 0
            });

            // Transform the new API response format into the internal format
            const normalizedResponse = this.transformer.normalizeFlightApiResponse(response.data);

            // Validate the normalized response
            if (!this.transformer.validateNormalizedResponse(normalizedResponse)) {
                throw new Error('Invalid response structure from FlightApi.io');
            }

            return normalizedResponse;

        } catch (error) {
            console.error('FlightApiProvider: Search failed', error);

            if (error.code === 'ECONNABORTED') {
                throw new Error('FlightApi.io request timed out. Please try again.');
            } else if (error.response) {
                const status = error.response.status;
                const statusText = error.response.statusText;

                switch (status) {
                    case 400:
                        throw new Error('Invalid search parameters provided to FlightApi.io');
                    case 401:
                        throw new Error('FlightApi.io API key is invalid or expired');
                    case 403:
                        throw new Error('Access denied to FlightApi.io service');
                    case 429:
                        throw new Error('Too many requests to FlightApi.io. Please wait and try again.');
                    case 500:
                    case 502:
                    case 503:
                        throw new Error('FlightApi.io service is temporarily unavailable');
                    default:
                        throw new Error(`FlightApi.io error: ${status} ${statusText}`);
                }
            } else if (error.request) {
                throw new Error('Unable to connect to FlightApi.io. Please check your internet connection.');
            } else {
                throw new Error(`FlightApi.io search error: ${error.message}`);
            }
        }
    }

    /**
     * Validate search parameters
     * @param {Object} params - Parameters to validate
     */
    validateSearchParams(params) {
        const { origin, destination, departureDate, returnDate, adults, currency } = params;

        if (!origin || origin.length !== 3) {
            throw new Error('Invalid origin airport code. Must be 3-letter IATA code.');
        }

        if (!destination || destination.length !== 3) {
            throw new Error('Invalid destination airport code. Must be 3-letter IATA code.');
        }

        if (!departureDate || !/^\d{4}-\d{2}-\d{2}$/.test(departureDate)) {
            throw new Error('Invalid departure date. Must be in YYYY-MM-DD format.');
        }

        if (!returnDate || !/^\d{4}-\d{2}-\d{2}$/.test(returnDate)) {
            throw new Error('Invalid return date. Must be in YYYY-MM-DD format.');
        }

        if (!adults || adults < 1 || adults > 9) {
            throw new Error('Invalid number of adults. Must be between 1 and 9.');
        }

        if (!currency || currency.length < 3) {
            throw new Error('Invalid currency code.');
        }

        // Validate dates are not in the past
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const depDate = new Date(departureDate);
        const retDate = new Date(returnDate);

        if (depDate < today) {
            throw new Error('Departure date cannot be in the past.');
        }

        if (retDate < depDate) {
            throw new Error('Return date must be after departure date.');
        }
    }

    /**
     * Get provider information
     * @returns {Object} Provider details
     */
    getInfo() {
        return {
            name: 'FlightApi.io',
            version: '1.0',
            description: 'Production flight search provider',
            baseUrl: this.baseUrl,
            features: [
                'Round-trip flights',
                'Economy class',
                'Up to 8 passengers',
                'Multiple currencies'
            ]
        };
    }

    /**
     * Test the connection to FlightApi.io
     * @returns {Promise<boolean>} True if connection is successful
     */
    async testConnection() {
        try {
            // Simple test with minimal parameters
            await this.search({
                origin: 'NYC',
                destination: 'LAX',
                departureDate: '2024-06-01',
                returnDate: '2024-06-08',
                adults: 1,
                currency: 'USD'
            });
            return true;
        } catch (error) {
            console.error('FlightApiProvider: Connection test failed', error);
            return false;
        }
    }
}